import { css } from "@emotion/react";
import IconOpen from "@mui/icons-material/ArrowForwardTwoTone";
import Delete from "@mui/icons-material/DeleteTwoTone";
import EditNote from "@mui/icons-material/EditNoteRounded";
import TagIcon from "@mui/icons-material/Sell";
import { Chip, IconButton } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { theme } from "../../design/theme";
import type { GetPublicationsOutput } from "../../utils/trpc";
import { trpc } from "../../utils/trpc";
import { getAnnotationId } from "../utils";
import type { AnnotationWithTags } from "./annotations/utils";
import { renderHighlightedText } from "./annotations/utils";
import { PrimaryButton } from "./buttons";
import { publicationCSS } from "./cssHelpers";
import {
  FlexColContainer,
  FlexRowContainer,
  SeparatorLineVertical,
} from "./layout";
import { Modal } from "./Modal";
import { TagManagementModal } from "./tags/TagManagementModal";
import { FRJ, type FRJOrUnknownPublication } from "./bookmarks/utils";
import { useUser } from "../hooks/useUser";

type TagOption = { id: number; name: string };

interface AnnotationProps {
  annotation: AnnotationWithTags;
  publication?: GetPublicationsOutput[0] | FRJOrUnknownPublication;
  label?: string;
  tags?: TagOption[];
}

export const AnnotationView = ({
  annotation,
  publication,
  label,
  tags = [],
}: AnnotationProps) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [noteText, setNoteText] = useState("");
  const [editingAnnotation, setEditingAnnotation] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { user } = useUser();
  const frjArticleWithoutAccess =
    user?.frjAccess === false && publication?.title === FRJ;

  const deleteAnnotations = trpc.deleteAnnotations.useMutation({
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      setEditingAnnotation(false);
      setNoteText("");
    },
  });

  const updateAnnotation = trpc.updateAnnotation.useMutation({
    onSuccess: () => {
      setEditingAnnotation(false);
      setNoteText("");
    },
  });

  const handleUpdate = () => {
    if (annotation.noteText !== noteText) {
      updateAnnotation.mutate({
        annotationId: annotation.id,
        noteText,
      });
    }
    setEditingAnnotation(false);
  };

  const sortedTags = useMemo(
    () => [...tags].sort((a, b) => a.name.localeCompare(b.name)),
    [tags],
  );
  const sortedAnnotationTags = useMemo(
    () =>
      [...(annotation.tags ?? [])].sort((a, b) =>
        a.tag.name.localeCompare(b.tag.name),
      ),
    [annotation.tags],
  );

  useEffect(() => {
    if (editingAnnotation && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [annotation.id, editingAnnotation]);

  return (
    <div
      css={({ colours }) => css`
        padding: 2px;
        border-radius: 6px 6px 6px 6px;
        opacity: 1;
        background-color: ${colours.uIGrey.light._10};
        width: 100%;
      `}
    >
      <div
        css={({ layers }) => css`
          border-radius: 6px;
          padding: 6px;
          ${layers.relief.uiGrey_10.shadow}
        `}
      >
        <div
          css={({ colours }) => css`
            box-shadow:
              0px 0px 0px 0.5px rgba(252, 251, 248, 1),
              0px -5px 10px -1px rgba(244, 243, 237, 1);
            opacity: 1;
            background-color: rgba(250, 249, 244, 1);
            border-left: 4px solid ${colours.highlight.yellow};
            padding-left: 8px;
            border-radius: 4px;
            margin: 0 auto;
            ${publicationCSS()}
            h1 {
              padding-top: 0;
            }
            h2 {
              padding-top: 0;
            }
            .footnote-toggle {
              cursor: text;
            }
          `}
        >
          <div style={theme.customTypography.book.label._3}>
            {renderHighlightedText(
              annotation.highlightedText,
              publication && "filename" in publication
                ? (publication.filename ?? "")
                : FRJ,
            )}
          </div>
        </div>
        {editingAnnotation ? (
          <textarea
            ref={textareaRef}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onBlur={handleUpdate}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                if (noteText !== annotation.noteText) {
                  handleUpdate();
                }
                e.currentTarget.blur();
              }
            }}
            style={{
              width: "100%",
              padding: theme.spacing(1),
              borderRadius: "4px",
              backgroundColor: theme.colours.uIGrey.light._5,
              border: `1px solid ${theme.colours.uIGrey.light._20}`,
              resize: "none",
              ...theme.customTypography.paragraph._2,
              outline: "none",
              scrollbarWidth: "thin",
              marginTop: "8px",
              height: "120px",
            }}
          />
        ) : (
          annotation.noteText && (
            <p
              style={{
                ...theme.customTypography.paragraph._2,
                color: theme.colours.accent.dark._50,
                paddingTop: "8px",
                whiteSpace: "pre-wrap",
              }}
            >
              {annotation.noteText}
            </p>
          )
        )}
        {
          <FlexRowContainer
            css={css`
              flex-wrap: wrap;
              justify-content: flex-start;
              width: 100%;
            `}
          >
            {sortedAnnotationTags.map(({ tag, id }) => (
              <Chip
                key={id}
                label={tag.name}
                size="small"
                style={{
                  marginTop: `${annotation.noteText ? "0" : "6px"}`,
                  maxWidth: "180px",
                  overflow: "hidden",
                }}
              />
            ))}
          </FlexRowContainer>
        }
      </div>
      <FlexRowContainer>
        <div
          style={{
            padding: "4px 0px 4px 8px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "1%",
            flexGrow: "1",
            justifyContent: "flex-start",
            ...theme.customTypography.label._4,
            color: theme.colours.accent.dark._40,
          }}
        >
          {label ?? (
            <div
              css={({ breakpoints }) => css`
                display: flex;
                gap: 4px;
                @media (max-width: ${breakpoints.values.md}px) {
                  flex-direction: column;
                }
              `}
            >
              <span>{`${
                publication?.title
                  ? `${publication.title}`
                  : "Removed publication"
              }`}</span>
              <span
                css={({ breakpoints }) => css`
                  @media (max-width: ${breakpoints.values.md}px) {
                    display: none;
                  }
                `}
              >{` ● `}</span>

              <span>{`${annotation.sectionTitle}`} </span>
            </div>
          )}
        </div>
        <FlexRowContainer style={{ gap: "0" }}>
          <IconButton
            onClick={() => {
              setNoteText(annotation.noteText || "");
              setEditingAnnotation(!editingAnnotation);
            }}
            title="Edit annotation note"
            size="small"
            sx={{
              width: "24px",
              height: "24px",
              padding: "0",
              borderRadius: "6px",
            }}
          >
            <EditNote sx={{ height: "16px" }} />
          </IconButton>
          <SeparatorLineVertical />
          <TagManagementModal
            tags={sortedTags}
            icon={<TagIcon />}
            annotationId={annotation.id}
            activeTagIds={(annotation.tags ?? []).map((t) => t.tagId)}
          />
          <SeparatorLineVertical />
          <Modal
            key="deleteAnnotationModal"
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
            }}
            renderButton={() => (
              <IconButton
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={deleteAnnotations.isLoading}
                title="Delete annotation"
                size="small"
                sx={{
                  width: "24px",
                  height: "24px",
                  padding: "0",
                  borderRadius: "6px",
                }}
              >
                <Delete sx={{ height: "16px", width: "16px" }} />
              </IconButton>
            )}
            header={<h2>Delete Annotation</h2>}
            content={
              <FlexColContainer
                css={css`
                  gap: 20px;
                `}
              >
                {`Are you sure you want to delete this annotation?`}
              </FlexColContainer>
            }
            footer={
              <PrimaryButton
                onClick={() =>
                  deleteAnnotations.mutate({
                    annotationIds: [annotation.id],
                  })
                }
              >
                Delete
              </PrimaryButton>
            }
          />
          <SeparatorLineVertical />
          {publication && !frjArticleWithoutAccess && (
            <Link
              to={{
                pathname:
                  "filename" in publication
                    ? `/pub/${publication.filename}`
                    : `/article/${annotation.articleSlug}`,
                hash: annotation.range.startNodeId,
                search: `?annotationId=${getAnnotationId(annotation)}`,
              }}
              css={css`
                padding-right: 4px;
              `}
              title="View at source"
            >
              <IconButton
                size="small"
                sx={{
                  width: "24px",
                  height: "24px",
                  padding: "0",
                  borderRadius: "6px",
                }}
              >
                <IconOpen sx={{ height: "16px", width: "16px" }} />
              </IconButton>
            </Link>
          )}
        </FlexRowContainer>
      </FlexRowContainer>
    </div>
  );
};
